/**
 * @file sticky-bar.ts
 * @description Нижняя полоса действия документа: маркер и прокрутка
 * «над полосой» (phase19.24 этап 1, шаг 1; дефект повторного QA 2026-09-20).
 *
 * На `<lg` главное действие документа живёт в полосе снизу (`WmsCommitBar`,
 * `PickProgressBar`) — правило warehouse-UI. Полоса прилипшая (`sticky`),
 * поэтому пока страница длиннее экрана, она по-прежнему висит над контентом:
 * поле, которое только что появилось, может оказаться ПОД ней — кладовщик
 * видит поле, жмёт и попадает в «Провести». Так было с «№ партии» в приёмке
 * на 360px (замер ui-visual-qa: поле 731, полоса 728).
 *
 * Замер `getBoundingClientRect` верен и для `sticky`, и для `fixed`: он всегда
 * возвращает коробку там, где элемент нарисован сейчас. Складские полосы пока
 * `sticky` (`WmsCommitBar`, `PickProgressBar`), полосы заказов — `fixed` по
 * общей геометрии `STICKY_BAR_CLASS`; хелпер одинаково работает с обеими.
 *
 * Маркер один на все полосы (`stickyBarProps`), чтобы хелпер не знал имён
 * компонентов: новая полоса = тот же спред пропсов, а не ветка здесь. Нет
 * видимой полосы (мы на `lg`) — зона видимости заканчивается краем окна, и
 * прокрутки не будет: на desktop экран ничего не перекрывает.
 */

/** Атрибут-маркер нижней полосы: `<div {...stickyBarProps} />`. */
export const STICKY_BAR_ATTR = "data-wms-sticky-bar";

/** Пропсы-маркер для нижней полосы действия (см. `ensureVisibleAboveBar`). */
export const stickyBarProps = { [STICKY_BAR_ATTR]: "" };

/**
 * Геометрия нижней полосы действия на `<lg`.
 *
 * `fixed`, а не `sticky`: прилипшая полоса живёт последним элементом потока и
 * на середине прокрутки ложится поверх карточек — на 360px под ней оказывались
 * радио «Способ получения» и «Заметки», и тап попадал в кнопку отправки
 * (дефект QA 5.1). Фиксированная полоса стоит на одном месте, а место под неё
 * страница резервирует отступом (`STICKY_BAR_SPACER`) — контент прокручивается
 * ВЫШЕ полосы и остаётся нажимаемым.
 *
 * Горизонтально полоса живёт по колонке контента: левый край берётся из
 * `--app-bar-left` (`AppShell` кладёт туда ширину постоянного сайдбара —
 * на 768px он уже развёрнут), отступы повторяют `p-4 sm:p-6 md:p-8`. Фон
 * непрозрачный — иначе текст карточек читался бы сквозь кнопку.
 */
export const STICKY_BAR_CLASS =
  "fixed bottom-0 right-0 left-[var(--app-bar-left,0px)] " +
  "z-[var(--z-sticky-bar)] flex flex-col gap-1 " +
  "border-t border-outline-variant bg-surface-lowest/95 backdrop-blur " +
  "px-4 pt-2 sm:px-6 md:px-8 " +
  "pb-[calc(0.5rem+env(safe-area-inset-bottom))] lg:hidden";

/**
 * Резерв места под полосой у страницы, которая её показывает.
 *
 * 56px кнопка + отступы полосы + строка причины блокировки: без резерва
 * последний блок страницы невозможно долистать из-под полосы.
 */
export const STICKY_BAR_SPACER =
  "pb-[calc(7rem+env(safe-area-inset-bottom))] lg:pb-0";

/** Видимая полоса действия на экране (на `lg` её коробка нулевая). */
function findBar(): DOMRect | null {
  const bar = document.querySelector<HTMLElement>(`[${STICKY_BAR_ATTR}]`);
  const rect = bar?.getBoundingClientRect();
  return rect && rect.height > 0 ? rect : null;
}

/** Нижняя граница зоны, где кладовщик видит и может нажать элемент. */
function visibleLimit(): number {
  return findBar()?.top ?? window.innerHeight;
}

/**
 * Поднимает элемент в видимую зону НАД полосой действия.
 *
 * Ничего не делает, если элемент и так виден целиком: прокрутка без причины
 * сбивает кладовщику список позиций.
 */
export function ensureVisibleAboveBar(el: HTMLElement | null | undefined): void {
  // В jsdom `scrollIntoView` не реализован — тесты не должны падать на вёрстке.
  if (!el || typeof el.scrollIntoView !== "function") return;
  const rect = el.getBoundingClientRect();
  // Нулевая коробка — элемент ещё не отрисован (или скрыт): скроллить нечего.
  if (rect.height === 0) return;
  if (rect.top >= 0 && rect.bottom <= visibleLimit()) return;
  el.scrollIntoView({ block: "center", behavior: "smooth" });
}

/**
 * Панель раскрылась (выбрали фасовку) — показать раскрывшиеся поля.
 *
 * Блок показываем ЦЕЛИКОМ, если он помещается над полосой: иначе кладовщик
 * заполняет количество, а «№ партии» и «Ячейка» остаются за краем, и он не
 * понимает, что строка ещё не полная. Блок выше зоны видимости центрировать
 * нельзя — первое поле (с курсором) уехало бы за верхний край, поэтому в этом
 * случае поднимаем только его.
 */
export function ensureFieldsVisibleAboveBar(
  block: HTMLElement | null | undefined,
  firstField: HTMLElement | null | undefined,
): void {
  // Полосы нет (desktop) — перекрывать нечего, хватает обычной проверки.
  if (!findBar()) {
    ensureVisibleAboveBar(firstField);
    return;
  }
  const height = block?.getBoundingClientRect().height ?? 0;
  ensureVisibleAboveBar(height > 0 && height <= visibleLimit() ? block : firstField);
}
